import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, post } from "../api";
import { Alert, Button, Field, Panel, PanelHeader } from "./ui";

/** Table names as people say them: [one, many]. */
const LABEL: Record<string, [string, string]> = {
  external_ref: ["JobNimbus link", "JobNimbus links"],
  import_record: ["import review row", "import review rows"],
  import_run: ["pull", "pulls"],
  file_copy_run: ["file copy run", "file copy runs"],
  job_stage_history: ["stage change", "stage changes"],
  contact_link: ["related contact", "related contacts"],
  contact_compliance: ["sub paperwork record", "sub paperwork records"],
  reminder_log: ["reminder record", "reminder records"],
  qb_outbox: ["QuickBooks outbox entry", "QuickBooks outbox entries"],
  comm_message: ["text or call", "texts and calls"],
  drive_link: ["Drive link", "Drive links"],
  file_share: ["share link", "share links"],
  permit_inspection: ["inspection", "inspections"],
  measurement: ["measurement report", "measurement reports"],
  user_account: ["user", "users"],
  user_session: ["sign-in", "sign-ins"],
  user_role: ["role assignment", "role assignments"],
  role_permission: ["role permission", "role permissions"],
  permission_override: ["individual permission", "individual permissions"],
  setting: ["setting", "settings"],
  workflow_stage: ["workflow stage", "workflow stages"],
  push_subscription: ["notification device", "notification devices"],
  push_key: ["notification key", "notification keys"],
  qb_connection: ["QuickBooks connection", "QuickBooks connections"],
  geocode: ["saved map lookup", "saved map lookups"],
};
const label = (t: string, n: number) => {
  const [one, many] = LABEL[t] ?? [t.replace(/_/g, " "), t.replace(/_/g, " ").replace(/y$/, "ie") + "s"];
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
};

/**
 * A permanent removal with two steps: see exactly what goes (counts), then type the phrase.
 * Used for Start over (JobNimbus import) and Reset everything (testing only).
 */
export function ResetCard({ title, description, previewUrl, resetUrl, phrase, button, done, onDone }: { title: string; description: string; previewUrl: string; resetUrl: string; phrase: string; button: string; done: string; onDone?: () => void }) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const preview = useMutation({ mutationFn: () => post<{ counts: Record<string, number> }>(previewUrl) });
  const reset = useMutation({
    mutationFn: () => post<{ counts: Record<string, number> }>(resetUrl, { confirm: typed }),
    onSuccess: () => {
      if (onDone) return onDone();
      setTyped("");
      preview.reset();
      void qc.invalidateQueries();
    },
  });
  const counts = Object.entries(preview.data?.counts ?? {}).sort((a, b) => b[1] - a[1]);
  return (
    <Panel>
      <PanelHeader title={title} description={description} />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {(preview.error || reset.error) && <Alert>{errorMessage(preview.error ?? reset.error)}</Alert>}
        {reset.isSuccess && <Alert tone="ok">{done}</Alert>}
        {!preview.data ? (
          <div>
            <Button variant="danger" busy={preview.isPending} onClick={() => preview.mutate()}>
              See what would be removed
            </Button>
          </div>
        ) : (
          <>
            {counts.length ? (
              <p>
                This permanently removes: <strong>{counts.map(([t, n]) => label(t, n)).join(", ")}</strong>. It can't be undone.
              </p>
            ) : (
              <p className="text-muted">There's nothing to remove.</p>
            )}
            {counts.length > 0 && (
              <form
                className="flex flex-wrap items-end gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  reset.mutate();
                }}
              >
                <Field label={`Type ${phrase} to confirm`} value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} className="min-w-64" />
                <Button type="submit" variant="danger" busy={reset.isPending} disabled={typed !== phrase}>
                  {button}
                </Button>
                <Button variant="quiet" onClick={() => (preview.reset(), setTyped(""))}>
                  Cancel
                </Button>
              </form>
            )}
          </>
        )}
      </div>
    </Panel>
  );
}
